import type { TranslationTree } from '../../types';

export const toolView: TranslationTree = {
  toolView: {
    list: {
      pageTitle: 'Tool Selection',
      breadcrumb: {
        tools: 'Tools',
      },
      actions: {
        refresh: 'Refresh',
        scan: 'Scan Tool',
        expandAll: 'Expand all',
        collapseAll: 'Collapse all',
        showMore: 'Show more results',
      },
      tabs: {
        hierarchy: 'Hierarchy view',
        table: 'Table view',
      },
      filters: {
        title: 'Filters',
        reset: 'Reset',
        activeCount: '{{count}} active',
        search: {
          label: 'Search',
          placeholder: 'Search by reference or tool number',
        },
        reference: {
          label: 'Reference',
          all: 'All references',
        },
        health: {
          label: 'Health Status',
          any: 'All statuses',
        },
      },
      hierarchy: {
        error: 'Unable to load tools',
        retry: 'Retry',
        empty: {
          title: 'No tools available',
          description: 'No reference or tool was found in the system.',
        },
        toolCount: '{{count}} tools',
        toggleDisplay: 'Toggle display of {{reference}}',
        openTool: 'Open tool {{toolId}} details',
        noTrace: 'Never used',
      },
      table: {
        headers: {
          tool: 'Tool',
          reference: 'Reference',
          lastTrace: 'Last trace',
          state: 'State',
        },
        error: 'Unable to load tools',
        retry: 'Retry',
        empty: 'No tool matches the selected filters.',
        footer: {
          references: '{{count}} reference(s)',
          tools: '{{count}} tool(s)',
          noData: 'No data available',
        },
        openTool: 'Open tool {{toolId}} details',
        noTrace: 'Never used',
      },
      traceCount: {
        one: 'trace',
        many: 'traces',
      },
      pagination: {
        showing: 'Showing {{count}} of {{total}} items',
        showMore: 'Show more',
      },
    },
    rul: {
      title: 'Remaining useful life (RUL)',
      comingSoon: {
        title: 'Remaining useful life — coming soon',
        description:
          'The predictive maintenance model is not yet connected. This feature will be available once the ML endpoint is ready.',
      },
    },
    addToolDialog: {
      title: 'Register tool',
      description:
        'This tool ID was not found in the database. Fill in the details to register it now.',
      scannedId: 'Scanned ID',
      fields: {
        reference: {
          label: 'Internal reference',
          placeholder: 'e.g. REF-TC4-Ø12',
        },
        company: {
          label: 'Company name',
          placeholder: 'e.g. Sandvik',
        },
      },
      actions: {
        add: 'Add tool',
        cancel: 'Cancel',
      },
      errors: {
        default: 'Failed to register tool. Please try again.',
      },
    },
    scanDialog: {
      title: 'Scan Tool',
      description: 'Choose your preferred scanning method to identify the tool.',
      helper: 'Select a tab above to begin scanning.',
      tabs: {
        camera: 'Camera',
        keyence: 'Keyence',
        manual: 'Manual',
      },
      actions: {
        cancel: 'Close',
      },
    },
    detail: {
      description: "Review this tool's health, performance, and recent operations.",
      pageTitle: 'Tool Details',
      loadingSummary: 'Loading tool summary',
      actions: {
        showList: 'Show detailed list',
        showOverview: 'Return to overview',
        backToList: 'Back to list',
      },
      error: {
        invalidToolId: 'Invalid tool identifier',
        noToolIdProvided: 'No tool identifier was provided in the URL.',
        authenticationRequired: 'Authentication required',
        initializingConnection: 'Initializing connection…',
      },
    },
    metadata: {
      title: 'Tool metadata',
      fields: {
        id: 'Identifier',
        reference: 'Internal reference',
        state: 'State',
        machine: 'Most active machine',
      },
      fallback: {
        notProvided: 'Not provided',
      },
    },
    traces: {
      errors: {
        unknown: "Couldn't load the tool's traceability.",
        noSummary: 'No usable trace found for this tool.',
      },
      table: {
        title: 'Recent operations',
        description: 'Latest traceability entries for this tool.',
        empty: 'No trace recorded for this tool.',
        headers: {
          operator: 'Operator',
          duration: 'Duration',
          pieces: 'Parts',
          date: 'Date',
          state: 'State',
          operation: 'Operation',
        },
        notProvided: 'N/A',
      },
    },
    advanced: {
      title: 'Detailed trace log',
      showEmpty: 'Show empty fields',
      loading: 'Loading trace details…',
      noTraces: 'No traces recorded for this tool.',
      errors: {
        noDetails: 'No additional details available for this tool.',
      },
      navigator: {
        prev: 'Previous trace',
        next: 'Next trace',
        counter: '{{current}} / {{total}}',
        viewMode: 'View',
        editMode: 'Edit',
        modeLabel: 'Display mode',
      },
      editMode: {
        banner: 'Editing — unsaved changes will be lost on navigation',
        save: 'Save trace',
        discard: 'Discard changes',
        saving: 'Saving…',
        saveError: 'Could not save the trace. Please try again.',
      },
      sections: {
        general: 'General',
        machine: 'Machine',
        material: 'Material',
        lubrication: 'Lubrication',
        clamping: 'Clamping',
        holder: 'Tool holder',
        breakState: 'Break / State',
      },
      detail: {
        machine: 'Machine',
        machineBrand: 'Machine brand',
        machineModel: 'Machine model',
        machineSerial: 'Machine serial number',
        machineKafkaName: 'Signal source',
        material: 'Material',
        operator: 'Operator',
        pieces: 'Machined parts',
        distance: 'Machined distance',
        volume: 'Removed volume',
        malround: 'Out-of-roundness',
        pieceDuration: 'Per-part duration (min)',
        totalDuration: 'Total duration (min)',
        lubBrand: 'Coolant brand',
        lubType: 'Coolant type',
        clampingBrand: 'Clamping force',
        clampingType: 'Clamping type',
        holderBrand: 'Toolholder brand',
        holderType: 'Toolholder type',
        operation: 'Operation',
        materialPlaceholder: 'Search materials…',
        materialSearchPlaceholder: 'Search…',
        materialNoResults: 'No materials found',
        materialLotPlaceholder: 'Material lot',
        materialLotSelectFirst: 'Select a material first',
        materialLotNoLots: 'No lots for this material',
        toolState: 'Tool state',
        toolNote: 'Note',
        changeReasons: 'Change reasons',
      },
    },
    family: {
      mill: 'End mill',
      drill: 'Drill',
      turning: 'Turning tool',
      reamer: 'Reamer',
      tap: 'Tap',
      tool: 'Tool',
    },
    dataPanel: {
      title: {
        performance: 'Performance',
        conditions: 'Usage conditions',
      },
      perf: {
        totalDuration: 'Total duration',
        pieces: 'Parts machined',
        durationPerPiece: 'Duration / part',
        volume: 'Material volume',
        distance: 'Distance',
        traces: 'Traces recorded',
      },
      env: {
        machineModel: 'Machine model',
        operation: 'Operation',
        vc: 'Cutting speed (vc)',
        fz: 'Feed per tooth (fz)',
        ap: 'Axial depth (ap)',
        ae: 'Radial depth (ae)',
        malround: 'Malround',
        lubType: 'Lubricant type',
        lubBrand: 'Lubricant brand',
        holderType: 'Holder type',
        clampingType: 'Clamping type',
      },
    },
  },
};
